import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import Loading from '../../components/Loading';
import axios from '../../services/axios';
import { cancelChargeGroup } from '../../services/finance/billingApi';
import { formatBRL } from '../../utils/format';
import { financeErrorMessage } from '../Finance/utils/apiError';
import CancelReasonModal from '../Finance/components/CancelReasonModal';
import BankAccountTag from '../Finance/components/BankAccountTag';

import { Container } from '../../styles/layout/Container';
import {
   PageHeader,
   PageHeaderText,
   PageTitle,
   PageSubtitle,
} from '../../styles/layout/PageHeader';
import {
   Section,
   SectionBody,
   SectionHeader,
   SectionTitle,
} from '../../styles/layout/Section';
import { FormGrid } from '../../styles/layout/FormGrid';
import { Field } from '../../styles/forms/Field';
import { TableWrapper } from '../../styles/table/TableWrapper';
import { Table } from '../../styles/table/Table';
import {
   CtaButton,
   DangerButton,
   GhostButton,
   SmallButton,
   TabButton,
} from '../../styles/ui/Button';
import { EntityActionBar, ActionBarSpacer } from '../../styles/ui/ActionBar';

// ─── Estilos locais ───────────────────────────────────────────
// Caixa que imita o input desabilitado da ficha, para exibir conteúdo rico
// (a empresa emissora tem duas linhas: apelido + CNPJ) sem virar um <input>.
const ReadOnlyBox = styled.div`
   display: flex;
   align-items: center;
   min-height: 40px;
   padding: 6px 10px;
   border-radius: 10px;
   border: 1px solid ${({ theme }) => theme?.colors?.border ?? 'rgba(0,0,0,0.15)'};
   background: #dbdbdb;
   overflow: hidden;
`;

const StatusPill = styled.span`
   display: inline-flex;
   align-items: center;
   justify-content: center;
   padding: 4px 10px;
   border-radius: 999px;
   font-size: 11px;
   font-weight: 800;
   border: 1px solid ${({ $color }) => $color ?? 'rgba(0,0,0,0.15)'};
   background: ${({ $bg }) => $bg ?? 'rgba(0,0,0,0.06)'};
   color: ${({ $text }) => $text ?? '#444'};
`;

// Balão com contador para atendimentos e alterações
const BadgeButton = styled.button`
   display: inline-flex;
   align-items: center;
   gap: 4px;
   padding: 3px 8px;
   border-radius: 999px;
   font-size: 11px;
   font-weight: 700;
   border: 1px solid
      ${({ $variant }) =>
         $variant === 'update'
            ? 'rgba(2,136,209,0.4)'
            : 'rgba(156,39,176,0.4)'};
   background: ${({ $variant }) =>
      $variant === 'update' ? 'rgba(2,136,209,0.08)' : 'rgba(156,39,176,0.08)'};
   color: ${({ $variant }) => ($variant === 'update' ? '#0277bd' : '#6a1b9a')};
   cursor: pointer;
   transition: opacity 0.15s;

   &:hover {
      opacity: 0.75;
   }
`;

// Tabs
const TabBar = styled.div`
   display: flex;
   gap: 0;
   border-bottom: 2px solid rgba(0, 0, 0, 0.08);
   margin-bottom: 16px;
`;

const Tab = TabButton;

// Timeline
const TimelineFeed = styled.div`
   display: flex;
   flex-direction: column;
   gap: 10px;
   max-height: 480px;
   overflow-y: auto;
   padding-right: 4px;
`;

const TimelineItem = styled.div`
   display: flex;
   gap: 12px;
   padding: 12px 14px;
   border-radius: 8px;
   background: ${({ $type }) =>
      $type === 'update' ? 'rgba(2,136,209,0.05)' : 'rgba(156,39,176,0.05)'};
   border: 1px solid
      ${({ $type }) =>
         $type === 'update' ? 'rgba(2,136,209,0.15)' : 'rgba(156,39,176,0.15)'};
`;

const TimelineIcon = styled.div`
   font-size: 16px;
   line-height: 1;
   margin-top: 2px;
   flex-shrink: 0;
`;

const TimelineMeta = styled.div`
   font-size: 11px;
   color: #888;
   margin-top: 4px;
`;

const TimelineParcel = styled.span`
   display: inline-block;
   font-size: 10px;
   font-weight: 700;
   padding: 1px 6px;
   border-radius: 4px;
   background: rgba(0, 0, 0, 0.06);
   color: #555;
   margin-right: 6px;
`;

const STATUS_CONFIG = {
   PENDING: {
      label: 'Pendente',
      bg: 'rgba(255,193,7,0.15)',
      border: 'rgba(255,193,7,0.5)',
      text: '#7d5a00',
   },
   RECEIVED: {
      label: 'Recebido',
      bg: 'rgba(46,125,50,0.12)',
      border: 'rgba(46,125,50,0.35)',
      text: '#1b5e20',
   },
   OVERDUE: {
      label: 'Vencido',
      bg: 'rgba(211,47,47,0.12)',
      border: 'rgba(211,47,47,0.35)',
      text: '#b71c1c',
   },
   CANCELLED: {
      label: 'Cancelado',
      bg: 'rgba(0,0,0,0.06)',
      border: 'rgba(0,0,0,0.15)',
      text: '#444',
   },
   REFUNDED: {
      label: 'Estornado',
      bg: 'rgba(123,31,162,0.1)',
      border: 'rgba(123,31,162,0.3)',
      text: '#4a148c',
   },
};

function StatusBadge({ value }) {
   const cfg = STATUS_CONFIG[value] ?? { label: value };
   return (
      <StatusPill $bg={cfg.bg} $color={cfg.border} $text={cfg.text}>
         {cfg.label}
      </StatusPill>
   );
}

function formatDate(value) {
   if (!value) return '—';
   const [y, m, d] = String(value).substring(0, 10).split('-');
   return `${d}/${m}/${y}`;
}

function formatDateTime(value) {
   if (!value) return '—';
   const d = new Date(value);
   return d.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
   });
}

function formatCurrency(value) {
   return Number(value || 0).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
   });
}

// ─── Componente principal ─────────────────────────────────────
export default function BillingGroupShow() {
   const { groupId } = useParams();
   const navigate = useNavigate();
   const location = useLocation();

   // Restaura a listagem filtrada (a URL da lista carrega filtros/ordenação/
   // página); deep-link direto cai no fallback (navegacao.md §1.6).
   const handleBack = () => {
      if (location.key !== 'default') navigate(-1);
      else navigate('/billing');
   };

   const [group, setGroup] = useState(null);
   const [isLoading, setIsLoading] = useState(true);

   // Tab ativa: 'parcelas' | 'log'
   const [activeTab, setActiveTab] = useState('parcelas');

   // Timeline do grupo (feed mesclado de todas as parcelas)
   const [groupFeed, setGroupFeed] = useState([]);
   const [loadingFeed, setLoadingFeed] = useState(false);

   // cancelamento do grupo
   const [showCancelModal, setShowCancelModal] = useState(false);
   const [isCancelling, setIsCancelling] = useState(false);

   // Carrega a timeline de todas as parcelas do grupo
   const fetchGroupFeed = async (charges) => {
      if (!charges?.length) return;
      setLoadingFeed(true);
      try {
         // Busca timeline de cada parcela em paralelo
         const results = await Promise.all(
            charges.map((c) =>
               axios
                  .get(`/billing/${c.id}/timeline`)
                  .then((r) =>
                     (r?.data?.data?.feed ?? []).map((item) => ({
                        ...item,
                        // Injeta qual parcela originou o item
                        _installment_number: c.installment_number,
                        _installment_count: c.installment_count,
                        _charge_id: c.id,
                     }))
                  )
                  .catch(() => [])
            )
         );

         // Mescla todos os feeds e ordena por data desc
         const merged = results
            .flat()
            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

         setGroupFeed(merged);
      } finally {
         setLoadingFeed(false);
      }
   };

   // `cancelReason` chega validado do modal compartilhado (mín. 8 caracteres) e
   // é propagado pelo backend a todas as parcelas canceladas.
   const handleCancelGroup = async (cancelReason) => {
      setShowCancelModal(false);
      setIsCancelling(true);

      setGroup((prev) => ({
         ...prev,
         charges: prev.charges.map((c) =>
            c.status === 'PENDING' || c.status === 'OVERDUE'
               ? { ...c, status: 'CANCELLED', collection_status: 'CANCELADA' }
               : c
         ),
      }));

      try {
         setIsLoading(true);
         const { cancelled, errors } = await cancelChargeGroup(
            groupId,
            cancelReason
         );

         if (errors?.length) {
            toast.warn(
               `${cancelled.length} boleto(s) cancelado(s). ${errors.length} falhou: ${errors.map((e) => `#${e.id} (${e.error})`).join(', ')}`
            );
            const cancelledIds = new Set(cancelled);
            setGroup((prev) => ({
               ...prev,
               charges: prev.charges.map((c) =>
                  !cancelledIds.has(c.id) && c.status === 'CANCELLED'
                     ? { ...c, status: 'PENDING', collection_status: 'ATIVA' }
                     : c
               ),
            }));
            setIsLoading(false);
         } else {
            setIsLoading(false);
            toast.success(
               `${cancelled.length} boleto(s) do grupo cancelados com sucesso.`
            );
         }
      } catch (err) {
         setGroup((prev) => ({
            ...prev,
            charges: prev.charges.map((c) =>
               c.status === 'CANCELLED' && c.collection_status === 'CANCELADA'
                  ? { ...c, status: 'PENDING', collection_status: 'ATIVA' }
                  : c
            ),
         }));
         setIsLoading(false);
         // Passa pelo dicionário do Financeiro: cobre cancel_reason_required,
         // charge_pending_at_provider e charge_without_bank_account.
         toast.error(financeErrorMessage(err, 'Erro ao cancelar grupo.'));
      } finally {
         setIsCancelling(false);
      }
   };

   const POLL_INTERVAL = 2500;

   useEffect(() => {
      if (!group) return;
      if (group.charges?.length >= group.installment_count) return;

      const timer = setTimeout(async () => {
         try {
            const response = await axios.get(`/billing/group/${groupId}`);
            setGroup(response?.data?.data?.group);
         } catch {
            // falha silenciosa
         }
      }, POLL_INTERVAL);

      return () => clearTimeout(timer);
   }, [group, groupId]);

   useEffect(() => {
      const fetchGroup = async () => {
         try {
            const response = await axios.get(`/billing/group/${groupId}`);
            const g = response?.data?.data?.group;
            setGroup(g);
            // Pré-carrega feed quando todas as parcelas já chegaram
            if (g?.charges?.length >= g?.installment_count) {
               fetchGroupFeed(g.charges);
            }
         } catch (err) {
            toast.error('Erro ao carregar parcelamento.');
            if (err?.response?.status === 404) navigate('/billing');
         } finally {
            setIsLoading(false);
         }
      };
      fetchGroup();
   }, [groupId, navigate]);

   // Carrega feed ao trocar para a aba de log (caso não tenha carregado ainda)
   useEffect(() => {
      if (
         activeTab === 'log' &&
         groupFeed.length === 0 &&
         group?.charges?.length
      ) {
         fetchGroupFeed(group.charges);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [activeTab, group?.charges?.length, groupFeed.length]);

   if (isLoading)
      return (
         <Container>
            <Loading isLoading />
         </Container>
      );
   if (!group) return null;

   const receivedCount =
      group.charges?.filter((c) => c.status === 'RECEIVED').length ?? 0;
   const totalCount = group.charges?.length ?? 0;

   // Contadores totais do grupo (soma de todas as parcelas)
   const totalUpdates =
      group.charges?.reduce((s, c) => s + (c.updates_count ?? 0), 0) ?? 0;
   const totalChanges =
      group.charges?.reduce((s, c) => s + (c.changes_count ?? 0), 0) ?? 0;

   return (
      <Container>
         <Loading isLoading={isLoading} />
         {/* Motivo obrigatório (mín. 8 caracteres) — mesmo modal da ficha da
             cobrança; o motivo é gravado em cada parcela cancelada. */}
         <CancelReasonModal
            isOpen={showCancelModal}
            onClose={() => setShowCancelModal(false)}
            onConfirm={handleCancelGroup}
            isBusy={isCancelling}
            title="Cancelar parcelamento"
            confirmLabel="Confirmar cancelamento"
            description="Todos os boletos pendentes e vencidos deste parcelamento serão cancelados no ASAAS, todos com o motivo informado abaixo. Esta ação não pode ser desfeita."
         />

         <PageHeader>
            <PageHeaderText>
               <PageTitle>Parcelamento — {group.customer_name}</PageTitle>
               <PageSubtitle>
                  {group.order_number ? `Pedido ${group.order_number} · ` : ''}
                  {group.installment_count}x de{' '}
                  {formatCurrency(group.total_value / group.installment_count)}
                  {' · '}Total {formatCurrency(group.total_value)}
                  {' · '}
                  {receivedCount}/{totalCount} parcelas recebidas
                  {group.created_at
                     ? ` · Criado em ${formatDateTime(group.created_at)}`
                     : ''}
               </PageSubtitle>
            </PageHeaderText>

            {/* Action bar de entidade (FC7 §G.7): destrutivo isolado à
                esquerda, ação de destaque na ponta direita. */}
            <EntityActionBar>
               {group.charges?.some(
                  (c) => c.status === 'PENDING' || c.status === 'OVERDUE'
               ) && (
                  <>
                     <DangerButton
                        type="button"
                        onClick={() => setShowCancelModal(true)}
                        disabled={isCancelling}
                     >
                        Cancelar Parcelamento
                     </DangerButton>
                     <ActionBarSpacer />
                  </>
               )}
               {/* Voltar pelo histórico restaura a listagem filtrada de onde o
                   operador saiu; ir direto para /billing (como antes) jogava
                   fora a pesquisa. Deep-link cai no fallback. */}
               <GhostButton type="button" onClick={handleBack}>
                  Voltar
               </GhostButton>
               <CtaButton type="button" onClick={() => navigate('/billing/new')}>
                  Novo Boleto
               </CtaButton>
            </EntityActionBar>
         </PageHeader>

         {/* ─── Resumo do grupo ──────────────────────────────── */}
         <Section>
            <SectionHeader>
               <SectionTitle>Dados do Parcelamento</SectionTitle>
            </SectionHeader>
            <SectionBody>
               <FormGrid $cols={12} $colsMobile={12}>
                  <Field $col={4} $colMobile={6}>
                     <label>Cliente</label>
                     <input type="text" value={group.customer_name} disabled />
                  </Field>
                  <Field $col={2} $colMobile={6}>
                     <label>CPF / CNPJ</label>
                     <input
                        type="text"
                        value={group.customer_doc ?? '—'}
                        disabled
                     />
                  </Field>
                  {/* Empresa emissora — o backend traz `bankAccount` no GRUPO,
                      não repetido em cada parcela: as parcelas herdam a conta
                      do grupo por construção (handoff 0082 §2.3). */}
                  <Field $col={3} $colMobile={6}>
                     <label>Empresa emissora</label>
                     <ReadOnlyBox>
                        <BankAccountTag
                           account={group.bankAccount}
                           to={
                              group.bankAccount?.id
                                 ? `/finance/bank-accounts/${group.bankAccount.id}`
                                 : null
                           }
                        />
                     </ReadOnlyBox>
                  </Field>
                  <Field $col={2} $colMobile={6}>
                     <label>Nº Pedido</label>
                     <input
                        type="text"
                        value={group.order_number ?? '—'}
                        disabled
                     />
                  </Field>
                  <Field $col={2} $colMobile={6}>
                     <label>Nº Nota Fiscal</label>
                     <input
                        type="text"
                        value={group.nfe_number ?? '—'}
                        disabled
                     />
                  </Field>
                  <Field $col={2} $colMobile={6}>
                     <label>Valor Total</label>
                     <input
                        type="text"
                        value={formatCurrency(group.total_value)}
                        disabled
                     />
                  </Field>
                  <Field $col={3} $colMobile={6}>
                     <label>Origem</label>
                     <input
                        type="text"
                        value={group.billingOrigin?.name ?? '—'}
                        disabled
                     />
                  </Field>
                  <Field $col={2} $colMobile={6}>
                     <label>Nº Parcelas</label>
                     <input
                        type="text"
                        value={group.installment_count}
                        disabled
                     />
                  </Field>
                  <Field $col={2} $colMobile={6}>
                     <label>Valor por Parcela</label>
                     <input
                        type="text"
                        value={formatCurrency(
                           group.total_value / group.installment_count
                        )}
                        disabled
                     />
                  </Field>
                  <Field $col={5} $colMobile={12}>
                     <label>Descrição</label>
                     <input
                        type="text"
                        value={group.description ?? '—'}
                        disabled
                     />
                  </Field>
               </FormGrid>
            </SectionBody>
         </Section>

         {/* ─── Tabs: Parcelas / Log ─────────────────────────── */}
         <Section>
            <SectionHeader>
               <TabBar>
                  <Tab
                     $active={activeTab === 'parcelas'}
                     onClick={() => setActiveTab('parcelas')}
                  >
                     Parcelas
                  </Tab>
                  <Tab
                     $active={activeTab === 'log'}
                     onClick={() => setActiveTab('log')}
                  >
                     Log de Alterações e Atendimento
                     {totalUpdates + totalChanges > 0 && (
                        <span
                           style={{
                              marginLeft: 6,
                              fontSize: 10,
                              fontWeight: 800,
                              background: 'rgba(0,0,0,0.08)',
                              borderRadius: 999,
                              padding: '1px 6px',
                           }}
                        >
                           {totalUpdates + totalChanges}
                        </span>
                     )}
                  </Tab>
               </TabBar>
            </SectionHeader>

            <SectionBody>
               {/* ── Aba Parcelas ── */}
               {activeTab === 'parcelas' && (
                  <>
                     {group.charges?.length < group.installment_count ? (
                        <div
                           style={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              gap: 12,
                              padding: '32px 0',
                              color: '#888',
                           }}
                        >
                           <div
                              style={{
                                 width: 32,
                                 height: 32,
                                 border: '3px solid rgba(2,136,209,0.3)',
                                 borderTop: '3px solid #0288d1',
                                 borderRadius: '50%',
                                 animation: 'spin 0.8s linear infinite',
                              }}
                           />
                           <p style={{ margin: 0, fontSize: 14 }}>
                              Aguarde, gerando parcelas... (
                              {group.charges?.length ?? 0} de{' '}
                              {group.installment_count} criadas)
                           </p>
                           <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                        </div>
                     ) : (
                        <TableWrapper>
                           <Table>
                              <thead>
                                 <tr>
                                    <th>Parcela</th>
                                    <th>Vencimento</th>
                                    <th>Valor</th>
                                    {/* Handoff 0105 — formatBRL, não o
                                        formatCurrency local: null (não
                                        informado) é "—", nunca R$ 0,00. */}
                                    <th>Recebido</th>
                                    <th>Juros e multa</th>
                                    <th>Status</th>
                                    <th>Dt. Recebimento</th>
                                    <th style={{ textAlign: 'center' }}>
                                       Ações
                                    </th>
                                 </tr>
                              </thead>
                              <tbody>
                                 {group.charges
                                    ?.slice()
                                    .sort(
                                       (a, b) =>
                                          a.installment_number -
                                          b.installment_number
                                    )
                                    .map((c) => (
                                       <tr key={c.id}>
                                          <td style={{ textAlign: 'center' }}>
                                             {c.installment_number} de{' '}
                                             {c.installment_count}
                                          </td>
                                          <td style={{ textAlign: 'center' }}>
                                             {formatDate(c.due_date)}
                                          </td>
                                          <td style={{ textAlign: 'right' }}>
                                             {formatCurrency(
                                                c.installment_value
                                             )}
                                          </td>
                                          <td style={{ textAlign: 'right' }}>
                                             {formatBRL(c.amount_received)}
                                          </td>
                                          <td style={{ textAlign: 'right' }}>
                                             {formatBRL(c.surcharge_amount)}
                                          </td>
                                          <td style={{ textAlign: 'center' }}>
                                             <StatusBadge value={c.status} />
                                          </td>
                                          <td style={{ textAlign: 'center' }}>
                                             {formatDate(c.payment_date)}
                                          </td>

                                          {/* ── Coluna de Ações ── */}
                                          <td style={{ textAlign: 'center' }}>
                                             <div
                                                style={{
                                                   display: 'inline-flex',
                                                   gap: 6,
                                                   alignItems: 'center',
                                                }}
                                             >
                                                {/* Balão atendimentos */}
                                                <BadgeButton
                                                   type="button"
                                                   $variant="update"
                                                   title="Ver atendimentos desta parcela"
                                                   onClick={() =>
                                                      navigate(
                                                         `/billing/${c.id}`
                                                      )
                                                   }
                                                >
                                                   💬 {c.updates_count ?? 0}
                                                </BadgeButton>

                                                {/* Balão alterações */}
                                                <BadgeButton
                                                   type="button"
                                                   $variant="change"
                                                   title="Ver alterações desta parcela"
                                                   onClick={() =>
                                                      navigate(
                                                         `/billing/${c.id}`
                                                      )
                                                   }
                                                >
                                                   🔄 {c.changes_count ?? 0}
                                                </BadgeButton>

                                                {/* Ver boleto PDF */}
                                                {c.boleto_url ? (
                                                   <SmallButton
                                                      as="a"
                                                      href={c.boleto_url}
                                                      target="_blank"
                                                      rel="noreferrer"
                                                      title="Ver PDF do boleto"
                                                   >
                                                      PDF
                                                   </SmallButton>
                                                ) : (
                                                   <span
                                                      style={{
                                                         fontSize: 12,
                                                         color: '#aaa',
                                                      }}
                                                   >
                                                      —
                                                   </span>
                                                )}

                                                {/* Ver cobrança */}
                                                <SmallButton
                                                   type="button"
                                                   title="Abrir detalhe da parcela"
                                                   onClick={() =>
                                                      navigate(
                                                         `/billing/${c.id}`
                                                      )
                                                   }
                                                >
                                                   Abrir
                                                </SmallButton>
                                             </div>
                                          </td>
                                       </tr>
                                    ))}
                              </tbody>
                           </Table>
                        </TableWrapper>
                     )}
                  </>
               )}

               {/* ── Aba Log ── */}
               {activeTab === 'log' && (
                  <>
                     {loadingFeed ? (
                        <div
                           style={{
                              textAlign: 'center',
                              padding: '32px 0',
                              color: '#888',
                              fontSize: 14,
                           }}
                        >
                           Carregando histórico...
                        </div>
                     ) : groupFeed.length === 0 ? (
                        <div
                           style={{
                              textAlign: 'center',
                              padding: '32px 0',
                              color: '#aaa',
                              fontSize: 14,
                           }}
                        >
                           Nenhum registro de atendimento ou alteração
                           encontrado.
                        </div>
                     ) : (
                        <TimelineFeed>
                           {groupFeed.map((item, idx) => (
                              // eslint-disable-next-line react/no-array-index-key
                              <TimelineItem
                                 key={`${item.type}-${item.id}-${idx}`}
                                 $type={item.type}
                              >
                                 <TimelineIcon>
                                    {item.type === 'update' ? '💬' : '🔄'}
                                 </TimelineIcon>
                                 <div style={{ flex: 1 }}>
                                    <div
                                       style={{
                                          fontSize: 13,
                                          color: '#333',
                                          lineHeight: 1.5,
                                       }}
                                    >
                                       <TimelineParcel>
                                          Parcela {item._installment_number}/
                                          {item._installment_count}
                                       </TimelineParcel>
                                       {item.type === 'update' ? (
                                          item.content
                                       ) : (
                                          <>
                                             <strong>{item.field_label}</strong>
                                             {': '}
                                             <span
                                                style={{
                                                   textDecoration:
                                                      'line-through',
                                                   color: '#999',
                                                }}
                                             >
                                                {item.old_value ?? '—'}
                                             </span>
                                             {' → '}
                                             <strong style={{ color: '#333' }}>
                                                {item.new_value ?? '—'}
                                             </strong>
                                             {item.source === 'webhook' && (
                                                <span
                                                   style={{
                                                      marginLeft: 6,
                                                      fontSize: 10,
                                                      color: '#888',
                                                   }}
                                                >
                                                   (via ASAAS
                                                   {item.asaas_event
                                                      ? `: ${item.asaas_event}`
                                                      : ''}
                                                   )
                                                </span>
                                             )}
                                          </>
                                       )}
                                    </div>
                                    <TimelineMeta>
                                       {item.author?.nome ?? 'Sistema'} ·{' '}
                                       {formatDateTime(item.created_at)}
                                       {' · '}
                                       <span
                                          style={{
                                             cursor: 'pointer',
                                             color: '#0288d1',
                                             textDecoration: 'underline',
                                          }}
                                          onClick={() =>
                                             navigate(
                                                `/billing/${item._charge_id}`
                                             )
                                          }
                                       >
                                          ver parcela
                                       </span>
                                    </TimelineMeta>
                                 </div>
                              </TimelineItem>
                           ))}
                        </TimelineFeed>
                     )}
                  </>
               )}
            </SectionBody>
         </Section>
      </Container>
   );
}
